import React, { useEffect, useState } from "react";
import { api, money } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ChevronDown, Download, Printer, Clock, DollarSign, CalendarDays } from "lucide-react";

const inputCls = "rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

// Monday of the week containing `d`
const mondayOf = (d) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // 0 = Monday
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
};
const fmt = (d) => d.toISOString().slice(0, 10);

export default function Payroll() {
  const { t } = useLang();
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [techId, setTechId] = useState("");
  const [techs, setTechs] = useState([]);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get("/users").then((u) => setTechs(u.filter((x) => x.role === "technician"))).catch(() => {});
  }, []);

  const load = () => {
    const qs = `week_start=${fmt(weekStart)}${techId ? `&tech_id=${techId}` : ""}`;
    api.get(`/timesheets?${qs}`).then(setData).catch(() => {});
  };
  useEffect(load, [weekStart, techId]); // eslint-disable-line

  const shiftWeek = (days) => { const d = new Date(weekStart); d.setDate(d.getDate() + days); setWeekStart(mondayOf(d)); };

  const downloadPdf = async () => {
    try {
      const qs = `week_start=${fmt(weekStart)}${techId ? `&tech_id=${techId}` : ""}`;
      const blob = await api.getBlob(`/timesheets/pdf?${qs}`);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch { toast.error(t("error")); }
  };

  const exportCsv = () => {
    if (!data) return;
    const rows = [["Employee", "Rate", "Date", "Clock In", "Clock Out", "Type", "Service Call", "Property", "Hours", "Cost"]];
    data.employees.forEach((g) => {
      g.entries.forEach((e) => {
        rows.push([g.tech_name, g.rate, e.date, (e.clock_in || "").slice(11, 16), (e.clock_out || "").slice(11, 16),
          e.type === "standby" ? "Standby" : "Job",
          e.ticket_title || "", e.unit_name || "", e.hours, e.cost]);
      });
      rows.push([`${g.tech_name} SUBTOTAL Jobs`, "", "", "", "", "", "", "", g.job_hours ?? 0, g.job_cost ?? 0]);
      rows.push([`${g.tech_name} SUBTOTAL Standby`, "", "", "", "", "", "", "", g.standby_hours ?? 0, g.standby_cost ?? 0]);
      rows.push([`${g.tech_name} TOTAL`, "", "", "", "", "", "", "", g.total_hours, g.total_cost]);
    });
    rows.push(["GRAND TOTAL", "", "", "", "", "", "", "", data.grand_total_hours, data.grand_total_cost]);
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `payroll-${fmt(weekStart)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const weekEnd = new Date(weekStart); weekEnd.setDate(weekEnd.getDate() + 6);
  const isThisWeek = fmt(mondayOf(new Date())) === fmt(weekStart);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{t("payroll")}</h1>
          <p className="text-slate-500 text-sm mt-0.5">{t("payrollHint")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="export-csv-btn" onClick={exportCsv} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium px-3 py-2 hover:bg-slate-50"><Download className="w-4 h-4" />{t("exportCsv")}</button>
          <button data-testid="print-pdf-btn" onClick={downloadPdf} className="inline-flex items-center gap-1.5 rounded-lg bg-orange-600 text-white text-sm font-semibold px-3 py-2 hover:bg-orange-700"><Printer className="w-4 h-4" />{t("printPdf")}</button>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <button data-testid="week-prev" onClick={() => shiftWeek(-7)} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronLeft className="w-4 h-4 text-slate-600" /></button>
          <button data-testid="week-this" onClick={() => setWeekStart(mondayOf(new Date()))} className={`px-3 py-2 rounded-lg text-sm font-medium border ${isThisWeek ? "border-slate-900 text-slate-900" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{t("thisWeek")}</button>
          <button data-testid="week-next" onClick={() => shiftWeek(7)} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronRight className="w-4 h-4 text-slate-600" /></button>
        </div>
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <CalendarDays className="w-4 h-4 text-slate-400" />
          {weekStart.toLocaleDateString()} — {weekEnd.toLocaleDateString()}
        </div>
        <div className="flex-1" />
        <select data-testid="employee-filter" value={techId} onChange={(e) => setTechId(e.target.value)} className={inputCls}>
          <option value="">{t("allEmployees")}</option>
          {techs.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4" data-testid="summary-hours">
          <div className="flex items-center justify-between"><span className="text-xs font-bold uppercase text-slate-500">{t("totalHours")}</span><span className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center"><Clock className="w-4 h-4" /></span></div>
          <div className="text-2xl font-bold text-slate-900 mt-2 tabular-nums">{data?.grand_total_hours ?? 0}h</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4" data-testid="summary-cost">
          <div className="flex items-center justify-between"><span className="text-xs font-bold uppercase text-slate-500">{t("totalCost")}</span><span className="w-9 h-9 rounded-full bg-green-50 text-green-600 flex items-center justify-center"><DollarSign className="w-4 h-4" /></span></div>
          <div className="text-2xl font-bold text-slate-900 mt-2 tabular-nums">{money(data?.grand_total_cost || 0)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4" data-testid="summary-employees">
          <div className="flex items-center justify-between"><span className="text-xs font-bold uppercase text-slate-500">{t("employee")}</span><span className="w-9 h-9 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center"><CalendarDays className="w-4 h-4" /></span></div>
          <div className="text-2xl font-bold text-slate-900 mt-2 tabular-nums">{data?.employees?.length ?? 0}</div>
        </div>
      </div>

      {/* Per-employee */}
      {data && data.employees.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-400">
          <Clock className="w-10 h-10 mx-auto mb-2 text-slate-300" />{t("noHours")}
        </div>
      )}
      <div className="space-y-4">
        {data?.employees.map((g) => {
          const open = expanded[g.tech_id] !== false; // default open
          return (
            <div key={g.tech_id} data-testid={`payroll-emp-${g.tech_id}`} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <button onClick={() => setExpanded({ ...expanded, [g.tech_id]: !open })} className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 text-left">
                <div className="flex items-center gap-3">
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "" : "-rotate-90"}`} />
                  <div>
                    <div className="font-semibold text-slate-900">{g.tech_name}</div>
                    <div className="text-xs text-slate-400 flex flex-wrap items-center gap-x-2">
                      <span>{money(g.rate)}/h</span>
                      <span className="text-slate-300">·</span>
                      <span data-testid={`emp-jobhrs-${g.tech_id}`}>{t("jobHours")}: {g.job_hours ?? 0}h</span>
                      <span className="text-indigo-500" data-testid={`emp-officehrs-${g.tech_id}`}>{t("officeHours")}: {g.standby_hours ?? 0}h</span>
                      {g.lunch_hours > 0 && <span className="text-slate-400">{t("lunchDeduction")}: −{g.lunch_hours}h</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right hidden sm:block">
                    <div className="text-[10px] uppercase font-bold text-slate-400">{t("jobsCol") || "Jobs"}</div>
                    <div className="text-sm font-semibold text-slate-800 tabular-nums">{g.job_hours ?? 0}h · <span className="text-green-700">{money(g.job_cost ?? 0)}</span></div>
                  </div>
                  <div className="text-right hidden sm:block">
                    <div className="text-[10px] uppercase font-bold text-indigo-500">{t("standbyCol") || "Standby"}</div>
                    <div className="text-sm font-semibold text-indigo-700 tabular-nums">{g.standby_hours ?? 0}h · {money(g.standby_cost ?? 0)}</div>
                  </div>
                  <div className="text-right"><div className="text-[10px] uppercase font-bold text-slate-400">{t("totalHours")}</div><div className="text-lg font-bold text-slate-900 tabular-nums">{g.total_hours}h</div></div>
                  <div className="text-right"><div className="text-[10px] uppercase font-bold text-slate-400">{t("totalCost")}</div><div className="text-lg font-bold text-green-700 tabular-nums">{money(g.total_cost)}</div></div>
                </div>
              </button>
              {open && (
                <div className="overflow-x-auto border-t border-slate-100">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                        <th className="text-left px-5 py-2">{t("date")}</th>
                        <th className="text-left px-3 py-2">{t("clockIn")}/{t("clockOut")}</th>
                        <th className="text-left px-3 py-2">{t("serviceCallCol")}</th>
                        <th className="text-left px-3 py-2">{t("byProperty")}</th>
                        <th className="text-right px-3 py-2">{t("hoursCol")}</th>
                        <th className="text-right px-5 py-2">{t("costCol")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {g.entries.map((e, i) => {
                        const isStandby = e.type === "standby";
                        return (
                          <tr key={i} data-testid={`payroll-entry-${g.tech_id}-${i}`} className={`hover:bg-slate-50 ${isStandby ? "bg-indigo-50/40" : ""}`}>
                            <td className="px-5 py-2 text-slate-700 tabular-nums">{e.date}</td>
                            <td className="px-3 py-2 text-slate-500 tabular-nums">{(e.clock_in || "").slice(11, 16)}{e.clock_out ? ` – ${e.clock_out.slice(11, 16)}` : ` – …`}</td>
                            <td className="px-3 py-2 text-slate-800">
                              {isStandby ? (
                                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 bg-indigo-100 rounded-full px-2 py-0.5">
                                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                                  {t("standbyOffice") || "Standby / Office"}
                                </span>
                              ) : e.ticket_title}
                            </td>
                            <td className="px-3 py-2 text-slate-500">{e.unit_name}</td>
                            <td className="px-3 py-2 text-right font-medium tabular-nums">{e.open ? <span className="text-amber-600 text-xs">open</span> : `${e.hours}h`}</td>
                            <td className="px-5 py-2 text-right font-medium tabular-nums text-slate-900">{money(e.cost)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
